import { Injectable } from '@angular/core';
import { getAuth } from 'firebase/auth';
import { addDoc, collection, doc, updateDoc } from 'firebase/firestore';
import { dataStore, db } from 'src/app/app.component';
import { LogType, Role } from 'src/app/enums';
import { AutomaticPlanningLog } from 'src/app/interfaces';

type AutomaticPlanningStatus = AutomaticPlanningLog['status'];

@Injectable({
  providedIn: 'root',
})
export class AutoPlanService {
  private index: number = 0;
  private lastChangeType: 'standard' | 'background' = 'standard';
  private lastDate: Date;

  setSelectedIndex(index: number): void {
    this.index = index;
  }

  setLastChangeType(type: 'standard' | 'background'): void {
    this.lastChangeType = type;
  }

  setLastDate(date: Date): void {
    this.lastDate = date;
  }

  getLastDate(): Date {
    return this.lastDate;
  }

  getLastChangeType(): 'standard' | 'background' {
    return this.lastChangeType;
  }

  getSelectedIndex(): number {
    if (this.lastChangeType === 'background') {
      this.index = 1;
      this.lastChangeType = 'standard';
    } else {
      this.index = 0;
    }
    return this.index;
  }

  async createAutomaticPlanningActionLog(
    type: LogType.AutomaticPlanningStarted | LogType.AutomaticPlanningReset,
    affectedDate: Date,
  ): Promise<string | null> {
    try {
      const user = getAuth().currentUser;
      const organisationId =
        localStorage.getItem('orgId') ||
        dataStore.selectedOrganisation?.id ||
        dataStore.rootUser?.selectedOrgId;
      if (!organisationId) {
        console.error('Could not resolve organisationId for automatic planning action log start.');
        return null;
      }

      const firstName = (dataStore.rootUser?.name ?? dataStore.orgUser?.name)?.trim();
      const lastName = (dataStore.rootUser?.lastName ?? dataStore.orgUser?.lastName)?.trim();
      const userName = [firstName, lastName].filter(Boolean).join(' ') || 'Unknown';
      const userEmail =
        user?.email || dataStore.rootUser?.email || dataStore.orgUser?.email || 'unknown';
      const role = this.getAutomaticPlanningLogRole();
      const normalizedAffectedDate = new Date(affectedDate);
      normalizedAffectedDate.setHours(0, 0, 0, 0);
      const logData: AutomaticPlanningLog = {
        type,
        date: new Date(),
        userName,
        affectedDate: normalizedAffectedDate,
        userEmail,
        userId: user?.uid || dataStore.rootUser?.id || dataStore.orgUser?.id || 'unknown',
        role,
        status: 'pending',
      };

      const logRef = await addDoc(
        collection(db, `eventLogs/${organisationId}/automaticPlanningActions`),
        logData,
      );
      return logRef.id;
    } catch (error) {
      console.error('Failed to create automatic planning action log.', error);
      return null;
    }
  }

  async updateAutomaticPlanningActionStatus(
    logId: string | null | undefined,
    status: Exclude<AutomaticPlanningStatus, 'pending'>,
  ): Promise<void> {
    if (!logId) {
      return;
    }

    try {
      const organisationId =
        localStorage.getItem('orgId') ||
        dataStore.selectedOrganisation?.id ||
        dataStore.rootUser?.selectedOrgId;
      if (!organisationId) {
        console.error(
          'Could not resolve organisationId for automatic planning action log finalize.',
        );
        return;
      }

      const payload: Pick<AutomaticPlanningLog, 'status'> = { status };

      await updateDoc(
        doc(db, `eventLogs/${organisationId}/automaticPlanningActions/${logId}`),
        payload,
      );
    } catch (error) {
      console.error('Failed to finalize automatic planning action log.', error);
    }
  }

  getAutoPlanLogStatus(resultStatus?: string): Exclude<AutomaticPlanningStatus, 'pending'> {
    if (!resultStatus) {
      return 'completed';
    }

    const completedStatuses = [
      'success',
      'no_generated_routes',
      'no_compatible_rides',
      'no_vehicles_to_plan',
      'no_rides_to_plan',
    ];

    return completedStatuses.includes(resultStatus) ? 'completed' : 'failed';
  }

  private getAutomaticPlanningLogRole(): string {
    if (dataStore.rootUser?.rights === 'admin') {
      return 'systemAdmin';
    }

    const roles = dataStore.orgUser?.roles ?? [];
    if (roles.includes(Role.Administrator)) {
      return Role.Administrator;
    }

    if (roles.includes(Role.Planner)) {
      return Role.Planner;
    }

    return roles.join(', ') || 'unknown';
  }
}
